/**
 * English UI catalogue for namespace 'inspector' (W2-06): the inspector panel and its sections, the Project
 * settings dialog, the text segment mode dialog, inspector commands and transaction labels.
 * Vocabulary follows DESIGN.md §2.5 ("Keyframe", "Stay, then jump", "Hide notes under the line"...); RPE and
 * community terms go into the info popovers (InfoPopover rpeTerm / communityTerm), not into these strings.
 */

import { catalogOf, defineMessages } from '../../i18n/define';

export const m = defineMessages('inspector', {
  // ------------------------------------------------------------------ panel
  panelTitle: 'Inspector',
  emptyTitle: 'Nothing selected',
  emptyBody: 'Select a note, a keyframe, a segment or a line. With nothing selected, the active line is shown here.',
  noProject: 'Open a project to inspect its notes and lines.',
  projectSettings: 'Project settings',
  rawToggle: 'Show the exact JSON the export writes (Raw)',
  rawUnavailable: 'The Raw panel is not available.',
  unitsFriendly: 'Friendly units (%, px, deg)',
  unitsRaw: 'RPE raw units',
  fullSectionTip: 'This section belongs to the Full level; it is shown because your selection needs it.',
  sectionFailed: 'This part of the inspector stopped working. Change the selection to try again.',
  panelLabel: 'Inspector panel',
  helpExpressionsTitle: 'Expressions in number fields',
  helpExpressionsBody:
    'Number fields accept expressions as well as numbers. With several items selected they are evaluated per item, in time order.\n\n- `+=10` adds 10, `*-1` flips the sign, `=0` sets every item\n- `lerp(-400, 400, u)` spreads values evenly (u goes from 0 to 1)\n- `i` is the position of the item, `n` the number of items, `v` its current value\n- `rand(-30, 30)` draws a random value; the seed is shown so the result can be reproduced\n\nDrag a field label to scrub: Shift is coarse, Alt is fine.',
  helpProjectSettingsTitle: 'Project settings',
  helpProjectSettingsBody:
    'Chart-wide settings live in one place: aspect ratio and background dim, how holds and note sizes render, the speed mode, the song-select preview and export defaults. Open it from the Project menu, the inspector header or the command palette.',
  helpUnitsTitle: 'Friendly and RPE units',
  helpUnitsBody:
    'The inspector shows values the way you see them: opacity in %, Length in % of the default line, sizes in %. At the Full level the units button switches every field to the raw numbers RPE writes (opacity 0-255, scale multipliers).',

  // ------------------------------------------------------------------ commands
  cmdProjectSettings: 'Project settings...',
  cmdFocusField: 'Focus an inspector field',
  cmdFocus: 'Focus the inspector',
  cmdToggleUnits: 'Inspector: switch friendly / RPE raw units',
  cmdSetAtPlayhead: 'Keyframe the selected lines at the playhead...',
  cmdClampPreview: 'Clamp the song-select preview to the song',
  cmdTextMode: 'Change the text segment mode...',
  argProperty: 'Property',
  argSection: 'Section',
  argField: 'Field',
  fieldNoteTime: 'Note time',
  fieldNoteX: 'Note X',
  fieldNoteSpeed: 'Note speed',
  fieldKeyBeat: 'Keyframe beat',
  fieldKeyValue: 'Keyframe value',
  fieldSegmentCurve: 'Segment curve',
  fieldLineName: 'Line name',
  fieldLineParent: 'Parent line',
  fieldLineZOrder: 'Line draw order',
  fieldSpeedMode: 'Speed mode (Project settings)',
  fieldPreview: 'Song-select preview (Project settings)',
  needProject: 'Open a project first.',
  needTextSegment: 'Select a text segment first.',
  needLines: 'Select one or more lines first.',
  previewAlreadyFits: 'The song-select preview already fits the song.',
  previewNoMusic: 'The song length is not known yet.',
  previewClamped: 'Song-select preview clamped to {start} s to {end} s.',
  previewBeyondAfterMusic:
    'The song-select preview now ends after the song ({limit} s): Phira would refuse to open the chart.',
  clampAction: 'Clamp',
  fieldNotShown: 'That field is not shown for the current selection.',

  // ------------------------------------------------------------------ transaction labels
  txEditNotes:
    '{field, select, x {Note X} speed {Note speed} yOffset {Note height offset} size {Note width} alpha {Note opacity} visibleTime {Note appear time} judgeArea {Note judge width} other {Edit notes}}',
  txNoteKind: 'Change note type',
  txNoteTime: 'Move notes in time',
  txNoteEnd: 'Change hold end',
  txNoteSide: 'Change note side',
  txNoteFake: 'Change decoration notes',
  txHitsound: 'Change hitsound',
  txTint: 'Change note colour',
  txTintHit: 'Change hit-effect colour',
  txKeyBeat: 'Move keyframes',
  txKeyValue: 'Change keyframe values',
  txKeyJump: 'Change keyframe jump',
  txDeleteKeys: 'Delete keyframes',
  txEase: 'Change curve',
  txStay: 'Change Stay, then jump',
  txSlice: 'Change curve slice',
  txTextMode: 'Change text segment mode',
  txLineName: 'Rename line',
  txLinesName: 'Rename lines',
  txLineType: 'Change line type',
  txParent: 'Change parent line',
  txRotateWithParent: 'Change also turn with parent',
  txZOrder: 'Change draw order',
  txCover: 'Change hide notes under the line',
  txGroup: 'Change line group',
  txTag: 'Change tag colour',
  txLineValue: 'Change {prop}',
  txTexture: 'Change line image',
  txAttachUi: 'Change HUD element',
  txText: 'Change line text',
  txGifAuto: 'Change Play automatically',
  txResetProp: 'Reset {prop}',
  txAbsentFix: 'Set {prop}',
  txIncline: 'Change note tilt',
  txControl: 'Change distance effect',
  txSetAtPlayhead: 'Keyframe {count, plural, one {# line} other {# lines}} at the playhead',
  txBpm: 'Change tempo point',
  txDeleteBpm: 'Delete tempo points',
  txMarker: 'Change marker',
  txDeleteMarkers: 'Delete markers',
  txMeta: 'Change song-select preview',
  txPkg: 'Change project settings',
  txSpeedMode: 'Switch speed mode',
  txClampPreview: 'Clamp song-select preview',

  // ------------------------------------------------------------------ command body failures
  exprFailed: 'The expression gives no number for item {index} of {count}.',
  exprNoNumbers: 'These keyframes hold no numbers.',
  setAtPlayheadNothing: 'None of these lines can take that property.',
  noHolds: 'None of the selected notes is a Hold.',
  sliceOrder: 'The start of the slice must be before its end.',

  // ------------------------------------------------------------------ shared words
  done: 'Done',
  cancel: 'Cancel',
  apply: 'Apply',
  none: 'None',
  seconds: 's',
  px: 'px',
  deg: 'deg',
  percent: '%',
  percentValue: '{value, number, percent}',
  pxValue: '{value} px',
  /** Two readouts shown as one line ("Phira: exact · RPE: exact"). */
  readoutPair: '{first} · {second}',
  times: 'x',

  // ------------------------------------------------------------------ properties
  propMoveX: 'Position X',
  propMoveY: 'Position Y',
  propRotate: 'Rotation',
  propAlpha: 'Opacity',
  propSpeed: 'Scroll speed',
  propLength: 'Length',
  propThickness: 'Thickness',
  propImageSizeX: 'Image size X',
  propImageSizeY: 'Image size Y',
  propTextSizeX: 'Text size X',
  propTextSizeY: 'Text size Y',
  propUiSizeX: 'UI size X',
  propUiSizeY: 'UI size Y',
  propBrushWidth: 'Brush width',
  propBrushHeight: 'Brush height',
  propColor: 'Colour',
  propText: 'Text',
  propIncline: 'Note tilt',
  propPaint: 'Brush',
  propGif: 'GIF progress',
  layerBase: 'L0',
  layerN: 'Layer {n}',

  // ------------------------------------------------------------------ notes
  noteTitle: '{kind, select, tap {Tap note} drag {Drag note} flick {Flick note} hold {Hold note} other {Note}}',
  notesTitle: '{count, plural, one {# note} other {# notes}}',
  countTap: '{count} Tap',
  countDrag: '{count} Drag',
  countFlick: '{count} Flick',
  countHold: '{count} Hold',
  onLines: '{count, plural, one {on # line} other {on # lines}}',
  notesDetail: '{kinds} {lines}',
  copyNotes: 'Copy',
  deleteNotes: 'Delete',
  noteType: 'Type',
  kindTap: 'Tap',
  kindDrag: 'Drag',
  kindFlick: 'Flick',
  kindHold: 'Hold',
  noteTypeInfo: 'Tap: touch it. Drag: just be on the line. Flick: swipe across it. Hold: keep touching until its end.',
  noteTime: 'Time',
  noteTimeHelp: 'When the note is hit. Type a beat like 12:3/4, seconds like 23.5s or +1/4 to move.',
  noteEnd: 'End',
  noteEndHelp: 'When the hold ends (at least one snap step after its start).',
  holdLength: 'Length {beats} beats, {sec} s',
  toPlayhead: 'Move to the playhead',
  toPlayheadMulti: 'The earliest note moves to the playhead; the others keep their distance.',
  noteX: 'X',
  noteXHelp: 'Position along the line in RPE pixels: -675 is the left edge, 675 the right edge.',
  noteXInfo: 'Where the note sits along its line, in RPE pixels.',
  noteSide: 'Side',
  sideTop: 'Top',
  sideBottom: 'Bottom',
  noteSideHelp: 'Which side of the line the note comes from.',
  noteSideInfo: 'Top notes fall onto the line from above; Bottom notes rise to it from below.',
  noteFake: 'Fake',
  noteFakeLabel: 'Decoration only',
  noteFakeDesc: 'Not judged, never counted, no hit sound.',
  noteFakeInfo: 'A decoration note is drawn but never judged and never counted.',
  noteMore: 'More',
  noteMoreInUse: 'Some of these fields are in use',
  noteSpeed: 'Speed',
  noteSpeedHelp: 'Multiplies how far the note travels: 2 approaches twice as fast.',
  noteSpeedInfo: "Per-note speed factor on top of the line's scroll speed.",
  noteYOffset: 'Height offset',
  noteYOffsetHelp: 'Extra distance from the line in RPE pixels (Phira scales it by the speed).',
  noteYOffsetInfo: 'Shifts the note away from the line without changing when it is hit.',
  noteWidth: 'Width',
  noteWidthHelp: '100 % is the normal note width.',
  noteWidthInfo: 'Width of the note; with "Width also scales note height" the height follows.',
  noteOpacity: 'Opacity',
  noteOpacityHelp: '0 % hides the note; it is still judged.',
  noteAppears: 'Appears',
  appearsAlways: 'Always',
  appearsBefore: 'Before the hit',
  noteAppearsSec: 'Seconds before the hit',
  noteAppearsHelp: 'The note pops up this many seconds before its hit (no fade).',
  noteAppearsInfo: 'How long before its hit the note becomes visible.',
  noteHitsound: 'Hitsound',
  hitsoundDefault: '{kind, select, flick {Flick} drag {Drag} other {Tap}} (default)',
  hitsoundTap: 'Tap sound',
  hitsoundDrag: 'Drag sound',
  hitsoundFlick: 'Flick sound',
  hitsoundAdd: 'Add a sound file...',
  hitsoundPlay: 'Play the hitsound',
  hitsoundInfo: 'The sound played when the note is hit; a custom file is loaded with the chart.',
  noteTint: 'Note colour',
  noteTintInfo: 'Tints the note texture (RPE 1.7 and later).',
  noteTintHit: 'Hit-effect colour',
  noteTintHitInfo: 'Colour of the hit particles of this note (RPE 1.7 and later).',
  noteJudge: 'Judge width',
  noteJudgeHelp: '100 % is the normal judging width (about 160 RPE pixels to each side).',
  noteJudgeInfo: 'How far beside the note a touch still hits it (RPE 1.7 and later).',
  resetDefault: 'Reset to the default',
  phiraLine: 'Phira: {simultaneous} · {visible}',
  phiraSimultaneous:
    '{count, plural, =0 {not simultaneous with another note} one {simultaneous with # other note} other {simultaneous with # other notes}}',
  phiraVisible: 'visible {sec} s before its hit',
  phiraVisibleAlways: 'visible from the start of the chart',
  phiraNeverVisible: 'never visible at the current speed',
  phiraUiMover: 'never drawn: its line is a UI mover',
  notesSimultaneous:
    '{count, plural, one {# of these notes is} other {# of these notes are}} simultaneous with another note',
  distribute: 'Distribute...',
  transform: 'Transform...',

  // ------------------------------------------------------------------ keys
  keyTitle: 'Keyframe',
  keysTitle: '{count, plural, one {# keyframe} other {# keyframes}}',
  keyOn: '{prop}, layer {layer}',
  keysOnProps: '{count, plural, one {on # property} other {on # properties}}',
  deleteKey: 'Delete keyframes',
  keyAt: 'At',
  keyAtHelp: 'The beat of the keyframe. +1/4 moves it later.',
  keyValue: 'Value',
  keyValueHelp: 'The value at this keyframe. Expressions work too: +=10, *-1.',
  keyText: 'Text',
  keyColour: 'Colour',
  keyJumpLabel: 'Arrive at one value, leave at another',
  keyJumpInfo: 'A jump changes the value instantly at this keyframe (two half-diamonds in the column).',
  keyArrive: 'Arrive at',
  keyLeave: 'Then',
  keyJumpFirst: 'The first keyframe has nothing to arrive from.',
  keyArriveFixed: 'Arrives from the Stay segment before it, so the arrive value is fixed.',
  keyLast: 'Last keyframe: the value stays after it.',
  keyUnitMixed: 'Values in RPE raw units: the selected properties use different units.',
  keyIgnoredByKind: 'Phira ignores this property on this line.',
  keyMixedTypes: 'These keyframes hold different kinds of values.',

  // ------------------------------------------------------------------ segments
  segmentTitle: 'Segment',
  segmentsTitle: '{count, plural, one {# segment} other {# segments}}',
  segmentSpan: '{from} to {to}',
  segmentCurve: 'Curve',
  segmentCurveHelp: 'How the value moves to the next keyframe.',
  segmentCurveInfo: 'The curve of the segment that starts at this keyframe.',
  changeCurve: 'Change...',
  curveLinear: 'Linear (RPE 1)',
  curveStay: 'Stay, then jump',
  curveBezier: 'Custom Bezier curve',
  curveMixed: 'Different curves',
  curveGuided: '{strength}, {shape} (RPE {id})',
  curveFull: '{name} (RPE {id})',
  curveSliced: '{name}, part {left} to {right}',
  strengthGentle: 'Gentle',
  strengthSoft: 'Soft',
  strengthMedium: 'Medium',
  strengthStrong: 'Strong',
  strengthStronger: 'Stronger',
  strengthExtreme: 'Extreme',
  strengthSharp: 'Sharp',
  strengthOvershoot: 'Overshoot',
  strengthSpringy: 'Springy',
  strengthBouncy: 'Bouncy',
  shapeSlowStart: 'slow start',
  shapeSlowEnd: 'slow end',
  shapeSlowBoth: 'slow both',
  segmentStay: 'Stay, then jump at the next keyframe',
  segmentStayInfo: "The value stays constant and jumps to the next keyframe's value when it is reached.",
  segmentFrom: 'From',
  segmentTo: 'To',
  segmentToFixed: 'A Stay segment keeps its value until the next keyframe.',
  segmentSlice: 'Use part of the curve',
  segmentSliceStart: 'Start',
  segmentSliceEnd: 'End',
  segmentSliceInfo: 'Uses only a part of the curve (RPE easingLeft / easingRight).',
  segmentSliceOnlyTween: 'Only curves from the list can be sliced.',
  segmentSliceSkipped:
    '{count, plural, one {# segment was} other {# segments were}} skipped: only curves from the list can be sliced.',
  segmentBake: 'Bake...',
  segmentReverse: 'Reverse',
  segmentExtrapolate: 'Extrapolate',
  segmentLink: 'Link',
  segmentUnlink: 'Unlink',
  segmentLinked:
    'Linked segments (group {group}). Every edit to one of these segments is written to all of them (times stay independent).',
  exactPhira: 'Phira: {state, select, exact {exact} baked {baked on export} nan {divides by zero} other {exact}}',
  exactRpe:
    'RPE: {state, select, exact {exact} differs {draws this curve differently} baked {baked on export} ignored {keeps an easing Phira ignores} other {exact}}',
  approxSplit: 'Split Expo / Elastic curve: within 0.1 % of the original curve. Bake it when exactness matters.',
  crossScreen: 'Notes cross the screen in {sec} s',
  crossScreenRange: 'Notes cross the screen in {from} s to {to} s',
  crossScreenNever: 'Notes never reach the line at this speed',
  nanSlice: 'This part of the curve makes Phira divide by zero (the value becomes NaN).',
  useWholeCurve: 'Use the whole curve',
  rpeDiffers: 'RPE draws easing {id} differently from Phira; the preview shows what Phira draws.',
  bakedSpeed: 'Classic speed mode: this eased speed is written as linear pieces on export.',
  ignoredEase: 'This speed segment keeps RPE easing {id} for RPE; Phira plays it linear (Classic speed mode).',
  openSpeedSettings: 'Speed mode...',
  segmentDuration: '{beats} beats, {sec} s',
  exactness: '{phira} · {rpe}',
  exactnessDuration: '{phira} · {rpe} · {duration}',
  easeSkipped:
    '{count, plural, one {# segment kept its curve} other {# segments kept their curve}}: that curve is not allowed there (scroll speed, text or effect rules).',
  noSegment: 'The last keyframe starts no segment.',

  // ------------------------------------------------------------------ text segments
  textSegTitle: 'Text segment',
  textMode: 'Mode',
  modeSwitch: 'Switch',
  modeTypeOn: 'Type on',
  modeErase: 'Erase',
  modeExtend: 'Extend',
  modeCounter: 'Counter',
  modeSwitchDesc: 'Shows the first text until the next keyframe, then switches.',
  modeTypeOnDesc: 'Reveals the text character by character.',
  modeEraseDesc: 'Removes the text character by character.',
  modeExtendDesc: 'Adds or removes the characters that differ (one text starts with the other).',
  modeCounterDesc: 'Counts from one number to another.',
  modeMixed: 'Different modes',
  textModeInfo: 'Phira picks the animation from the two texts; the mode rewrites them for you.',
  editMode: 'Edit...',
  textFrom: 'Text',
  textThen: 'Then',
  textTypes: 'Types',
  textErases: 'Erases',
  textStart: 'Start text',
  textEnd: 'End text',
  counterFrom: 'From',
  counterTo: 'To',
  counterInt: 'Shows whole numbers.',
  counterDec: 'Shows 3 decimals (one end is not a whole number).',
  guardCrash: 'Curves that overshoot make Phira crash on Type on and Erase: the curve is set to Linear.',
  guardCrashNow: 'This curve overshoots: Phira crashes on this segment. Choose a curve that stays inside the range.',
  guardWarn: 'This curve overshoots: Phira briefly shows extra or missing characters.',
  counterNotNumeric: 'The whole text must be a number. Put a label on a second text line.',
  textSplitApprox: 'Splitting this segment is approximate: Phira rounds the character count.',
  textPreview: 'Preview',
  textPreviewAt: '{p} %',
  textModeTitle: 'Text segment mode',
  textModeSegment: 'Segment {from} to {to}',
  textExtendNeedsPrefix: 'One text must start with the other.',
  textEmpty: 'Type the text first.',

  // ------------------------------------------------------------------ line
  lineTitle: 'Line',
  lineName: 'Name',
  lineType: 'Type',
  typeLine: 'Line',
  typeText: 'Text',
  typeImage: 'Image',
  typeGif: 'GIF',
  typePaint: 'Paint',
  typeUiMover: 'UI mover',
  typeFull: '{label} (Full)',
  lineTypeInfo: 'What the line draws. Changing it adds or removes the data Phira derives it from.',
  pickImage: 'Pick the image',
  pickGif: 'Pick the GIF',
  pickElement: 'Pick the HUD element',
  noImages: 'Add an image to the project first.',
  noGifs: 'Add a GIF image to the project first.',
  addImage: 'Add image...',
  assetOption: '{path}',
  chip: '{text}',
  typeLossTitle: 'Change the line type?',
  typeLossBody:
    'Changing {count, plural, one {this line} other {these # lines}} to {type} removes: {what}. You can undo it.',
  typeLossConfirm: 'Change type',
  lossTexture: 'the image',
  lossText: 'the text keyframes',
  lossPaint: 'the brush keyframes',
  lossGif: 'the GIF progress keyframes',
  lossAutoLoop: 'Play automatically',
  lossAttachUi: 'the HUD link',
  lineParent: 'Parent line',
  parentNone: 'None',
  parentBlockedSelf: 'This line',
  parentBlockedLoop: 'Would loop',
  parentMixed: 'Mixed',
  parentSearch: 'Find a line by number or name',
  parentShowing: '{shown, plural, one {Showing # line} other {Showing # lines}} of {total}. Type to narrow the list.',
  parentHelp: 'Opacity, colour, scale and scroll speed are never inherited.',
  parentInfo: "The line moves with its parent: its position is measured in the parent's frame.",
  parentPromptTitle: 'How should the lines follow their new parent?',
  parentKeepNumbers: 'Keep their numbers',
  parentKeepNumbersDesc: 'They move with the parent; their values stay the same (RPE behaviour).',
  parentKeepPlace: 'Keep them where they are now',
  parentKeepPlaceDesc: "Their position is converted into the parent's frame at the playhead.",
  rotateWithParent: 'Also turn with parent',
  rotateWithParentSentence:
    "The child's position always circles with the parent's rotation. This only decides whether the child's own angle turns too.",
  rotateNoParent: 'No effect without a parent line.',
  diagramBefore: 'Parent at 0 deg',
  diagramTurns: 'The child turns too',
  diagramKeeps: 'The child keeps its angle',
  lineZOrder: 'Draw order',
  lineZOrderHelp: 'Higher numbers draw on top. Notes always draw above every line. Ties follow export order.',
  lineZOrderInfo: 'Order in which lines are drawn.',
  zFront: 'Bring to front',
  zBack: 'Send to back',
  lineCover: 'Hide notes under the line',
  lineCoverHelp: 'Notes coming from the wrong side of the line stay hidden until they cross it.',
  lineCoverInfo: 'Hides notes that are behind the line (for example with negative speed).',
  lineCoverRaw: 'Imported value {raw}: anything but 1 means off.',
  coverDiagramOn: 'A note behind the line is hidden',
  coverDiagramOff: 'A note behind the line stays visible',
  lineGroup: 'Group',
  groupDefault: 'Default',
  lineGroupInfo: 'Line groups help organise lines; they are exported to RPE.',
  lineTag: 'Tag colour',
  lineTagInfo: 'Tints the lane header and the preview label. Editor only.',
  lengthHelp: '100 % is the default length of a Phira line.',
  thicknessPx: '{px} px thick',
  notSetPlaceholder: 'Not set ({what})',
  absentFullLength: 'full length',
  moreLineSettings: 'More line settings',
  propNotSet: 'Not set',
  thicknessHelp: 'A multiplier of the default thickness; the exact thickness in pixels is shown.',
  imageSizeHelp: '100 % is one image pixel per RPE pixel ({w} x {h} px image).',
  imageSizeHelpNoSize: '100 % is one image pixel per RPE pixel.',
  textSizeHelp: '100 % is 54 RPE pixels.',
  uiSizeHelp: 'Scales the HUD element.',
  brushHelp: 'Brush radius in device pixels; 0 or less clears the canvas.',
  brushSizeHelp: '100 % is the brush as Phira draws it without scale keyframes.',
  animatedHint: 'Animated: editing writes a keyframe at {beat}.',
  staticHint: 'One keyframe: the value holds for the whole chart.',
  lineImage: 'Image',
  lineImageMissing: '{path} (missing)',
  imagePhiraOnly: 'This image format loads in Phira only: RPE reads PNG and JPG.',
  imageCentred: 'Phira draws the image centred on the line position.',
  gifFirstFrame: 'A GIF without GIF progress or Play automatically shows its first frame only, like Phira.',
  hitsoundMissing: '{path} (not in the project)',
  gifRemoveWarn: 'Choosing an image that is not a GIF removes the GIF progress and Play automatically.',
  lineImageInfo: 'The image the line shows instead of the line graphic. PNG and JPG load in RPE too.',
  lineUiElement: 'HUD element',
  uiPause: 'Pause button',
  uiCombonumber: 'Combo number',
  uiCombo: 'COMBO label',
  uiScore: 'Score',
  uiBar: 'Progress bar',
  uiName: 'Song name',
  uiLevel: 'Level',
  uiRotationNote: "The element's own angle ignores the parent's rotation.",
  uiMoverInfo: 'The line moves a HUD element instead of drawing anything.',
  uiMoverNotes: 'This line moves a HUD element: notes on it would be invisible.',
  lineText: 'Text',
  lineTextAnimated:
    '{count, plural, one {# text keyframe} other {# text keyframes}}: editing changes the one at {beat}.',
  lineTextInfo: 'The text the line shows (several lines allowed).',
  gifAutoLoop: 'Play automatically',
  gifAutoLoopHelp: 'Loops the GIF at its natural speed from the start of the chart.',
  gifProgressInUse: 'This GIF follows its GIF progress keyframes; Play automatically removes them.',
  properties: 'Properties',
  propStatic: 'One keyframe: {value}',
  propAnimated: '{count, plural, one {# keyframe} other {# keyframes}}',
  propLayers: '{count} layers',
  propIgnored: 'Phira ignores it on this line',
  propReset: 'Reset {prop}: remove its keyframes',
  fixAlpha: 'Set 100 %',
  fixSpeed: 'Set 10',
  usesLabel: 'Uses:',
  usesLayers: '{count} layers',
  usesColor: 'colour',
  usesScale: 'scale',
  usesIncline: 'note tilt',
  usesControls: 'distance effects',
  usesText: 'text',
  usesPaint: 'paint',
  usesGif: 'GIF',
  usesUiMover: 'UI mover',
  usesShow: 'Show',

  // ------------------------------------------------------------------ advanced
  advanced: 'Advanced',
  incline: 'Note tilt',
  inclineHelp: 'Tilts notes by distance. Phira freezes it at its start value for the whole chart.',
  inclineInfo: 'Squeezes the X of notes depending on their distance from the line (not holds).',
  inclineAnimated: 'Animated note tilt (imported): Phira uses {deg} deg for the whole chart.',
  makeStatic: 'Make static',
  controls: 'Distance effects',
  controlsInfo: 'Multipliers that depend on how far a note is from the line (RPE note controls).',
  rpeOnly: 'RPE-only fields',
  rpeOnlyTip: 'Phira ignores these fields; they are kept for RPE.',
  bpmfactor: 'BPM factor',
  anchor: 'Anchor',
  skewControl: 'Skew control',
  skewDefault: 'RPE default',
  skewCustom: 'Custom',

  // ------------------------------------------------------------------ distance effects
  controlAlpha: 'Opacity by distance',
  controlSize: 'Width by distance',
  controlPos: 'X by distance',
  controlY: 'Distance by distance',
  controlAlphaDesc: 'Multiplies the note opacity.',
  controlSizeDesc: 'Multiplies the note width (holds too).',
  controlPosDesc: 'Multiplies the note X (not holds; the judging position does not move).',
  controlYDesc: 'Multiplies how far the note is drawn from the line.',
  controlNotSet: 'Not set: no effect.',
  controlAdd: 'Add',
  controlClear: 'Remove distance effect',
  controlPresets: 'Presets',
  presetFadeInNear: 'Fade in near the line',
  presetFadeOutNear: 'Fade out near the line',
  presetShrinkFar: 'Shrink far notes',
  presetCentreFar: 'Pull far notes to the centre',
  presetCompressFar: 'Bring far notes closer',
  controlInert: 'Inert in Phira',
  controlInertTip: 'Phira ignores a curve with exactly 2 points whose first point has value 1 and easing 1.',
  addThirdPoint: 'Add a third point',
  controlNoEffect: 'Every value is 1: no effect.',
  pointX: 'Distance',
  pointEasing: 'Curve',
  pointEasingButton: 'Curve to this point: {curve}. Change...',
  pointFar: '{x} px: as far as possible, the effect holds for every farther note.',
  pointFarShort: '∞',
  graphToFar: '{value} px, then to infinity',
  pointValue: 'Value',
  addPoint: 'Add point',
  removePoint: 'Remove point',
  graphLabel: '{name}: multiplier by note distance',

  // ------------------------------------------------------------------ N lines
  linesTitle: '{count, plural, one {# line} other {# lines}}',
  typeCount: '{count} {type}',
  namePattern: 'Name',
  namePatternHelp: "Pattern: '{i}' = 0 to {last}, '{n}' = 1 to {count}, '{name}' = the current name.",
  namePatternPreview: 'Becomes: {names}',
  zOrderExprHelp: 'A number, or an expression such as i or i+10 (i = position in export order).',
  setAtPlayhead: 'Set at playhead',
  setAtPlayheadHelp: 'Writes a keyframe at the playhead on every selected line.',
  setOpacity: 'Opacity...',
  setPosition: 'Position...',
  setRotation: 'Rotation...',
  setSpeed: 'Scroll speed...',
  setOnLines: 'Set on {count, plural, one {# line} other {# lines}}',
  keepCurrent: "Leave empty to keep each line's value (only add keyframes).",
  textureAll: 'Applies to every selected line.',

  // ------------------------------------------------------------------ tempo
  tempoTitle: 'Tempo point',
  temposTitle: '{count, plural, one {# tempo point} other {# tempo points}}',
  tempoBeat: 'Beat',
  tempoBeatZero: 'The first tempo point is always at beat 0.',
  tempoBpm: 'BPM',
  tempoSec: 'At {sec}',
  tempoPolicy: 'Later items',
  policyKeepBeats: 'Keep their beats',
  policyKeepTime: 'Keep their time',
  policyHelp:
    'Keep beats: notes and keyframes move in time with the tempo (RPE). Keep time: they stay where they are heard. Remembered for this project; Song & Timing uses the same choice.',
  deleteTempo: 'Delete point',
  deleteTempos: 'Delete points',
  tempoRetimed: '{count, plural, one {# item was} other {# items were}} re-timed ({snapped} snapped to 1/1920 beat).',
  openTiming: 'Open Song & Timing',

  // ------------------------------------------------------------------ markers
  markerTitle: 'Marker',
  markersTitle: '{count, plural, one {# marker} other {# markers}}',
  markerName: 'Name',
  markerBeat: 'Beat',
  markerColour: 'Colour',
  deleteMarker: 'Delete marker',
  deleteMarkers: 'Delete markers',

  // ------------------------------------------------------------------ project settings
  psTitle: 'Project settings',
  psDescription: 'Settings of the whole chart. Every change can be undone.',
  psDisplay: 'Display',
  psGameplay: 'Gameplay rendering',
  psSpeed: 'Speed mode',
  psPreview: 'Song-select preview',
  psExport: 'Export defaults',
  aspectRatio: 'Aspect ratio',
  aspectHelp: 'Width divided by height of the play field (16:9 = 1.778).',
  aspectPreset: 'Preset',
  aspectCustom: 'Custom',
  forceAspect: 'Force this aspect ratio',
  forceAspectHelp: 'Phira letterboxes the play field to this ratio on every screen.',
  backgroundDim: 'Background dim',
  backgroundDimHelp: 'How much darker the illustration is drawn behind the chart.',
  holdPartialCover: 'Holds stay visible until the tail passes',
  holdPartialCoverHelp: 'With Hide notes under the line, a hold is hidden only once its tail is behind the line.',
  noteUniformScale: 'Width also scales note height',
  noteUniformScaleHelp: 'Note width (and Width by distance) scales the note height too.',
  attachUiFix: 'Attach-UI fix',
  attachUiFixHelp: 'The HUD element pivots Phira uses for UI movers (on in charts Phira imports).',
  lineLength: 'Line length',
  lineLengthHelp: "Half-length of normal lines in chart units (Phira's default is 6).",
  speedClassic: 'Classic',
  speedRpe170: 'RPE 1.7 speed curves',
  speedClassicDesc:
    "Phira's default: scroll speed changes linearly between keyframes; other curves are baked on export.",
  speedRpe170Desc:
    "Speed follows any curve; other players' speed settings no longer matter, but RPE before 1.7 shows different speeds.",
  speedSubModern: 'RPEVersion {version}: speed follows the drawn curve.',
  speedSubLegacy: 'RPEVersion {version} (below 170): RPE before 1.7 integration.',
  switchToRpe170: 'Switch to RPE 1.7 speed curves...',
  switchToClassic: 'Switch to Classic speed...',
  switchTitle: 'Switch the speed mode',
  switchToRpe170Body:
    "Scroll speed will follow the curves you draw. Other players' speed settings no longer matter, but RPE before 1.7 shows different speeds.",
  switchToClassicBody: 'Eased speed segments stay as they are and are baked into linear pieces on export.',
  rpeVersionQuestion: 'This chart says RPEVersion {version}. Phira treats speed curves differently below 170:',
  rpeVersionRaise: 'Raise RPEVersion to 170',
  rpeVersionRaiseDesc: 'Speed follows the drawn curve.',
  rpeVersionKeep: 'Keep {version}',
  rpeVersionKeepDesc: 'RPE before 1.7 integration: speed = k * curve slope + b.',
  ignoredEaseQuestion:
    '{count, plural, one {# speed segment keeps} other {# speed segments keep}} an RPE easing that Phira ignores today:',
  ignoredEaseDrop: 'Drop them',
  ignoredEaseDropDesc: 'The chart plays exactly as now.',
  ignoredEaseApply: 'Apply them',
  ignoredEaseApplyDesc: 'Those easings take effect.',
  switchConfirm: 'Switch',
  rpeVersion: 'RPEVersion',
  rpeVersionHelp: 'The RPE version written to the chart (never raised automatically).',
  speedBakeTol: 'Speed bake tolerance',
  speedBakeTolHelp: 'Largest allowed floor-position error when eased speed is baked, in RPE pixels.',
  previewStart: 'Start',
  previewEnd: 'End',
  previewEndDefault: '15 s after the start',
  previewEndCustom: 'Custom end',
  previewHelp: 'The part of the song played in the song list.',
  previewPlay: 'Play the preview',
  previewStop: 'Stop',
  previewSongLength: 'Song length: {sec} s.',
  previewSongUnknown: 'Song length unknown (no music yet).',
  previewIssueStart: 'The start must be 0 or later.',
  previewIssueOrder: 'The end must be after the start.',
  previewIssueBeyond: 'Ends after the song (limit {limit} s): Phira refuses to open the chart.',
  previewClamp: 'Clamp to the song',
  previewOk: 'Fits the song.',
  exportProfile: 'Export profile',
  profileRpe: 'RPE-compatible',
  profileRpeDesc: 'Adds the padding RPE expects (4 layer slots, default fields, info.txt).',
  profileMinimal: 'Minimal',
  profileMinimalDesc: 'Smallest files for Phira only.',
  fileNaming: 'File names',
  namingReadable: 'Readable (chart.json, music.ogg)',
  namingRpeId: 'RPE id ({id}.json)',
  infoOffsetHelp: 'Sub-millisecond remainder written to info.yml: {sec} s.',
  psNoProject: 'Open a project to change its settings.',
});

export default catalogOf(m);
